// Shared "can this user see this nav item / section" check.
//
// Extracted from Sidebar's inline filter (Task #15 / Commit 6c.1b) so the
// Settings shell (Task #8 — settings consolidation) uses the exact same
// rule instead of a second, possibly-drifting copy.
//
// `requireAny: [...]` means "visible iff the user has at least one of these
// effective permissions." An empty/undefined list means "always visible."
//
// super_admin bypass: permissions are baked into the JWT at login, so a
// super_admin whose token predates a permission grant can carry a stale,
// non-empty `permissions` array that's missing the very perm we're checking
// for. Since super_admin can do everything by definition, it must always
// pass regardless of what's in that array — not just when the array happens
// to be empty (that was the old, incomplete "legacy-token" fallback below).
export function hasAnyPermission(user, requireAny) {
  if (!requireAny || requireAny.length === 0) return true

  if (user?.role === 'super_admin' || (user?.roles || []).includes('super_admin')) {
    return true
  }

  const perms = Array.isArray(user?.permissions) ? user.permissions : []
  return requireAny.some(p => perms.includes(p))
}

// RBAC Slice 3 — catalog cross-check helpers.
//
// The nav/header still gate on literal permission-NAME strings declared per
// item (a page always has to say which permission unlocks it — that part
// can't be eliminated), sourced from `user.permissions`, itself the live
// EFFECTIVE-permissions array `/api/auth/me` computes server-side
// (role ∪ grant − deny) — not a hardcoded role→page table. What CAN drift
// is the string itself: if a permission_name a nav item references is ever
// renamed or removed server-side (RBAC lives in migrations/DB, not just
// code), that nav item silently becomes unreachable for everyone, including
// super_admin, with no error. These two pure helpers are what
// hooks/useRbacDriftCheck.js uses to catch that against the live
// `GET /api/auth/rbac/catalog` response — see that hook for why it can only
// run for the subset of users allowed to fetch the catalog.

// Flattens every `requireAny` referenced across a list of nav-item-shaped
// objects (each optionally carrying its own `requireAny` and/or one level of
// `children`, each of which may carry `requireAny`) into a single de-duped,
// sorted list of permission-name strings.
export function collectRequireAnyPermissions(items = []) {
  const out = new Set()
  for (const item of items || []) {
    for (const p of item?.requireAny || []) out.add(p)
    for (const child of item?.children || []) {
      for (const p of child?.requireAny || []) out.add(p)
    }
  }
  return [...out].sort()
}

// Which of `referencedPermissions` (typically collectRequireAnyPermissions'
// output) are NOT present in `knownPermissionNames` (typically the live
// catalog's permission_name list) — i.e. which hardcoded gating strings
// have gone stale. Pure diff, no I/O, so it's exercised the same way
// whether the catalog came from a real fetch or a test fixture.
export function findUnknownPermissions(referencedPermissions, knownPermissionNames) {
  const known = new Set(knownPermissionNames || [])
  return (referencedPermissions || []).filter(p => !known.has(p))
}
